import { CSSProperties, FC, useMemo } from 'react';

type Direction = 'top' | 'right' | 'bottom' | 'left';

export type ArrowProps = {
  className?: string;
  direction: Direction;
  /**
   * a factor for the size of the arrow
   *
   * a size of 1 has ~23px on the longer edge,
   * a size of 10 has ~226px, and so on...
   */
  size?: number;
  /**
   * a number between 0.1 and 1.0 makes sense and looks good...
   * but basically every number works
   */
  aspectRatio?: number;
};

const anglesByDirection: Record<Direction, number> = {
  top: 135,
  right: -135,
  bottom: -45,
  left: 45,
};

const translationByDirection: Record<Direction, string> = {
  top: '33.3%, -66.6%',
  right: '0%, -33.3%',
  bottom: '33.3%, 0',
  left: '66.6%, -33.3%',
};

export const Arrow: FC<ArrowProps> = ({
  className,
  direction = 'right',
  size = 1,
  aspectRatio = 0.33,
}) => {
  const scale = ['right', 'left'].includes(direction)
    ? `scaleX(${aspectRatio})`
    : `scaleY(${aspectRatio})`;
  const translate = `translate(${translationByDirection[direction]})`;
  const rotate = `rotate(${anglesByDirection[direction]}deg)`;
  const borderWidth = Math.min(1 / aspectRatio, size * 4);

  const containerClassName = [className, 'relative'].join(' ');
  const containerStyles: CSSProperties = useMemo(
    () => ({
      transform: scale,
      width: `${size * 1.5}rem`,
      height: `${size * 1.5}rem`,
    }),
    [scale, size],
  );

  const arrowClassName = 'absolute border-gray-400';
  const arrowStyles: CSSProperties = useMemo(
    () => ({
      transform: `${rotate} ${translate}`,
      height: `${size}rem`,
      width: `${size}rem`,
      borderWidth: `0 0 ${borderWidth}px ${borderWidth}px`,
    }),
    [borderWidth, rotate, size, translate],
  );

  return (
    <div style={containerStyles} className={containerClassName}>
      <div style={arrowStyles} className={arrowClassName} />
    </div>
  );
};
